'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useCallback } from 'react';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { CLPPurchaseOption, useClpPurchaseContext, useClpSelectedOption } from '@/features/course-landing-page/context/clp-purchase-context.react-component';
import { BlockList } from '@udemy-v2/react-core-components';
import { useCommonData } from '@udemy-v2/common-data';
import { useClpNoticeContext } from '@/features/course-landing-page/context/clp-notice-context-provider.react-component';
import { publishEvent } from '@udemy-v2/event-tracking';
import { ExperimentImpressionEvent } from '@udemy-v2/react-experimentation';
import { Icon } from '@udemy-v2/icons';
import styles from './subscription-purchase-content.module.scss';
import { LocalizedHtml, useFormatNumber, useI18n } from '@udemy-v2/i18n';
import { CollapsiblePurchaseSection } from '@/features/course-landing-page/components/collapsible-purchase-section/collapsible-purchase-section.react-component';
import { aggregateCollectionStats, FALLBACK_COLLECTION_STATS, PlanPeriod, PlanPeriodStyle, SubscribeFromPageCookie, SubscriptionCTA, SubscriptionCTAClickEvent, SubscriptionLearnMoreEvent, SubscriptionOfferPresented, usePersonalPlanCollectionStats } from '@udemy-v2/react-subscription-browse';
import { SubscriptionPlanProductType } from '@udemy-v2/subscription-graphql';
import { useClpCourse } from '@/features/course-landing-page/context/clp-course-provider.react-component';
import { useClpSubscriptionContext } from '@/features/course-landing-page/context/clp-subscription-context.react-component';
import { BlendedOfferBadge } from '@/features/course-landing-page/components/blended-offer-badge/blended-offer-badge.react-component';
import { LifetimeAccessModal } from '@/features/course-landing-page/components/lifetime-access-modal/lifetime-access-modal.react-component';
import style from '../non-catalog-pp-offer/non-catalog-pp-offer.module.scss';
import { useFeatures } from '@udemy-v2/react-experimentation';
import { CollapsiblePurchaseBoxContent, SUBSCRIPTION_SUB_VALUE_PROP_MSG } from './collapsible-purchase-box-content.react-component';
const UI_REGION = 'purchase_section_tab0';
const PERSONAL_PLAN_COURSE_COUNT = 27000;
const SUBSCRIPTION_ATTRIBUTION_PATHS = ['/personal-plan', '/pricing'];
interface CategoryStats {
  isSubCopyEnabled?: boolean;
}
const CollapsibleSubscriptionPurchaseContentInner = categoryStats => {
  const $ = _c(98);
  const {
    gettext,
    interpolate
  } = useI18n();
  const {
    formatNumber
  } = useFormatNumber();
  const {
    unwrappedConsumerSubscriptionPlan,
    subscriptionCTAConfiguration,
    subscriptionTiersEnabled,
    isEligibleForBlendedOffer,
    blendedOfferFeature,
    clpGenericUdemyPlansCopyEnabled,
    starterPlanUpsellCLPCopyUpdatesEnabled
  } = useClpPurchaseContext();
  const {
    selectedPurchaseOption,
    setSelectedPurchaseOption
  } = useClpSelectedOption();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["change_cta_language_for_pp"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    featureVariantAssignments
  } = useFeatures(t0);
  const changeCtaLangFeatureAssignment = featureVariantAssignments.get("change_cta_language_for_pp")?.configuration as {
    getStarted: boolean;
    getPersonalPlan: boolean;
  } | undefined;
  const {
    isCourseInValuePlanCatalog,
    subscribedProductType
  } = useClpSubscriptionContext();
  const isGwgStandaloneSubscriber = subscribedProductType === SubscriptionPlanProductType.GrowWithGoogle;
  const course = useClpCourse();
  const commonData = useCommonData();
  const clpNoticeContext = useClpNoticeContext();
  const subscriptionPriceBoxNotice = clpNoticeContext?.subscriptionPriceBoxNoticeData;
  const contentCollections = unwrappedConsumerSubscriptionPlan?.contentCollections;
  const subscriptionPlanStats = contentCollections ? aggregateCollectionStats(contentCollections.map(_temp)) : FALLBACK_COLLECTION_STATS;
  const personalPlanStats = usePersonalPlanCollectionStats();
  const personalPlanCourseCount = personalPlanStats && "numberOfCourses" in personalPlanStats ? personalPlanStats.numberOfCourses : isGwgStandaloneSubscriber && contentCollections ? subscriptionPlanStats.numberOfCourses : PERSONAL_PLAN_COURSE_COUNT;
  const isPersonalPlanPrimaryPurchaseOptions = selectedPurchaseOption === CLPPurchaseOption.SUBSCRIPTION;
  const isStarterCourseInTiers = subscriptionTiersEnabled && isCourseInValuePlanCatalog;
  const hidePersonalPlanValuePropForStarterUpsell = isStarterCourseInTiers && starterPlanUpsellCLPCopyUpdatesEnabled;
  const formattedDiscountMonthlyPrice = unwrappedConsumerSubscriptionPlan?.selectedDiscount?.formattedSubscriptionDiscountMonthlyPrice;
  let t1;
  if ($[1] !== gettext || $[2] !== subscriptionPriceBoxNotice?.subtitle_link_text) {
    t1 = subscriptionPriceBoxNotice?.subtitle_link_text ?? gettext("Learn more");
    $[1] = gettext;
    $[2] = subscriptionPriceBoxNotice?.subtitle_link_text;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const learnMoreText = t1;
  const subscriptionContextLearnMoreUrl = unwrappedConsumerSubscriptionPlan?.urlLearnMore ?? "https://udemy.com/personal-plan/";
  const learnMoreUrl = isStarterCourseInTiers ? "https://udemy.com/pricing/" : subscriptionPriceBoxNotice?.subtitle_link_url ?? subscriptionContextLearnMoreUrl;
  const learnMoreTarget = subscriptionPriceBoxNotice?.subtitle_link_target === "new_tab" ? "_blank" : "_self";
  let t2;
  if ($[4] !== course.id || $[5] !== learnMoreUrl || $[6] !== unwrappedConsumerSubscriptionPlan) {
    t2 = () => {
      if (SUBSCRIPTION_ATTRIBUTION_PATHS.some(path => learnMoreUrl.includes(path))) {
        SubscribeFromPageCookie.set("course_landing_page", parseInt(course.id, 10));
      }
      if (unwrappedConsumerSubscriptionPlan) {
        publishEvent(new SubscriptionLearnMoreEvent({
          planId: unwrappedConsumerSubscriptionPlan.id,
          planType: unwrappedConsumerSubscriptionPlan.productType,
          uiRegion: UI_REGION,
          planPriceIds: unwrappedConsumerSubscriptionPlan.planPriceOptionIds
        }));
      }
    };
    $[4] = course.id;
    $[5] = learnMoreUrl;
    $[6] = unwrappedConsumerSubscriptionPlan;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const handleLearnMoreClick = t2;
  let t3;
  if ($[8] !== course.id || $[9] !== unwrappedConsumerSubscriptionPlan) {
    t3 = () => {
      SubscribeFromPageCookie.set("course_landing_page", parseInt(course.id, 10));
      if (unwrappedConsumerSubscriptionPlan) {
        publishEvent(new SubscriptionCTAClickEvent({
          planId: unwrappedConsumerSubscriptionPlan.id,
          planType: unwrappedConsumerSubscriptionPlan.productType,
          ctaType: unwrappedConsumerSubscriptionPlan.trialPeriodDays ? "trial" : "paid",
          uiRegion: UI_REGION,
          courseId: parseInt(course.id, 10),
          planPriceIds: unwrappedConsumerSubscriptionPlan.planPriceOptionIds
        }));
      }
    };
    $[8] = course.id;
    $[9] = unwrappedConsumerSubscriptionPlan;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const handleCtaClick = t3;
  let t4;
  if ($[11] !== commonData?.user?.country || $[12] !== course.frontendTrackingId || $[13] !== course.id || $[14] !== subscriptionTiersEnabled || $[15] !== unwrappedConsumerSubscriptionPlan) {
    t4 = subscriptionTiersEnabled && unwrappedConsumerSubscriptionPlan ? () => {
      publishEvent(new SubscriptionOfferPresented({
        subscriptionPlan: unwrappedConsumerSubscriptionPlan,
        productType: unwrappedConsumerSubscriptionPlan.productType,
        countryCode: commonData?.user?.country ?? "",
        courseId: parseInt(course.id, 10),
        courseTrackingId: course.frontendTrackingId ?? null,
        uiRegion: UI_REGION
      }));
    } : undefined;
    $[11] = commonData?.user?.country;
    $[12] = course.frontendTrackingId;
    $[13] = course.id;
    $[14] = subscriptionTiersEnabled;
    $[15] = unwrappedConsumerSubscriptionPlan;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const handleOfferImpression = t4;
  let t5;
  if ($[17] !== setSelectedPurchaseOption) {
    t5 = () => setSelectedPurchaseOption(CLPPurchaseOption.SUBSCRIPTION);
    $[17] = setSelectedPurchaseOption;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const handleExpand = t5;
  const showBlendedOfferBadge = isEligibleForBlendedOffer;
  const [isLifetimeAccessModalOpen, setIsLifetimeAccessModalOpen] = useState(false);
  const hasFiredBlendedOfferImpressionRef = useRef(false);
  let t6;
  if ($[19] !== blendedOfferFeature?.experimentIds || $[20] !== blendedOfferFeature?.isLocalDefault || $[21] !== showBlendedOfferBadge) {
    t6 = () => {
      if (showBlendedOfferBadge && !hasFiredBlendedOfferImpressionRef.current) {
        hasFiredBlendedOfferImpressionRef.current = true;
        publishEvent(new ExperimentImpressionEvent({
          experimentIds: blendedOfferFeature?.experimentIds ?? [],
          isLocalDefaultShown: blendedOfferFeature?.isLocalDefault ?? false
        }));
      }
    };
    $[19] = blendedOfferFeature?.experimentIds;
    $[20] = blendedOfferFeature?.isLocalDefault;
    $[21] = showBlendedOfferBadge;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== blendedOfferFeature || $[24] !== showBlendedOfferBadge) {
    t7 = [showBlendedOfferBadge, blendedOfferFeature];
    $[23] = blendedOfferFeature;
    $[24] = showBlendedOfferBadge;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  useEffect(t6, t7);
  if (!unwrappedConsumerSubscriptionPlan) {
    return null;
  }
  let t8;
  if ($[26] !== gettext) {
    t8 = gettext("Subscribe and save");
    $[26] = gettext;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  const titleText = t8;
  let t9;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = classNames("ud-text-md");
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== titleText) {
    t10 = <span className={styles["title-row"]}><span className={t9}>{titleText}</span></span>;
    $[29] = titleText;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== formattedDiscountMonthlyPrice || $[32] !== gettext || $[33] !== showBlendedOfferBadge || $[34] !== unwrappedConsumerSubscriptionPlan.formattedMonthlyAmount || $[35] !== unwrappedConsumerSubscriptionPlan.trialPeriodDays) {
    t11 = showBlendedOfferBadge ? <p><span className="ud-heading-xl">{unwrappedConsumerSubscriptionPlan.formattedMonthlyAmount}</span><span className="ud-text-sm">{gettext("/month, billed yearly")}</span></p> : <PlanPeriod listPriceText={unwrappedConsumerSubscriptionPlan.formattedMonthlyAmount} trialPeriodDays={unwrappedConsumerSubscriptionPlan.trialPeriodDays} style={PlanPeriodStyle.TITLE} discountPriceText={formattedDiscountMonthlyPrice} />;
    $[31] = formattedDiscountMonthlyPrice;
    $[32] = gettext;
    $[33] = showBlendedOfferBadge;
    $[34] = unwrappedConsumerSubscriptionPlan.formattedMonthlyAmount;
    $[35] = unwrappedConsumerSubscriptionPlan.trialPeriodDays;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  let t12;
  if ($[37] !== t10 || $[38] !== t11) {
    t12 = <>{t10}{t11}</>;
    $[37] = t10;
    $[38] = t11;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  const title = t12;
  let t13;
  if ($[40] !== gettext) {
    t13 = config => {
      if (!config) {
        return gettext("Start subscription");
      }
      if (config.getStarted && !config.getPersonalPlan) {
        return gettext("Get started");
      }
      if (!config.getStarted && config.getPersonalPlan) {
        return gettext("Get Personal Plan");
      }
      return gettext("Start subscription");
    };
    $[40] = gettext;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  const ctaText = t13;
  const T0 = CollapsiblePurchaseSection;
  let t14;
  if ($[42] !== isLifetimeAccessModalOpen || $[43] !== showBlendedOfferBadge || $[44] !== unwrappedConsumerSubscriptionPlan.urlTermsOfUse) {
    t14 = showBlendedOfferBadge && <><BlendedOfferBadge termsUrl={unwrappedConsumerSubscriptionPlan?.urlTermsOfUse} onLifetimeAccessClick={() => setIsLifetimeAccessModalOpen(true)} /><LifetimeAccessModal isOpen={isLifetimeAccessModalOpen} onClose={() => setIsLifetimeAccessModalOpen(false)} /></>;
    $[42] = isLifetimeAccessModalOpen;
    $[43] = showBlendedOfferBadge;
    $[44] = unwrappedConsumerSubscriptionPlan.urlTermsOfUse;
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  const T1 = BlockList;
  const t15 = "tight";
  const t16 = "small";
  let t17;
  if ($[46] !== categoryStats.isSubCopyEnabled || $[47] !== gettext) {
    t17 = categoryStats.isSubCopyEnabled && <div className={styles["value-prop-badge"]}><Icon className={style["value-prop-icon"]} glyph="info-outline" color="info" size="small" label="" /><LocalizedHtml html={gettext(SUBSCRIPTION_SUB_VALUE_PROP_MSG)} interpolate={{
        bold: <span className="ud-text-bold" />
      }} /></div>;
    $[46] = categoryStats.isSubCopyEnabled;
    $[47] = gettext;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  const t18 = categoryStats.isSubCopyEnabled && <CollapsiblePurchaseBoxContent coursesCount={formatNumber(isStarterCourseInTiers ? subscriptionPlanStats.numberOfCourses : subscriptionTiersEnabled ? personalPlanCourseCount : subscriptionPlanStats.numberOfCourses)} categoryName={course.categories[0]?.name} />;
  const t19 = !categoryStats.isSubCopyEnabled && <BlockList.Item icon={<Icon label="" size="xsmall" glyph={showBlendedOfferBadge ? "tick" : "video-playlist"} />}><span className="ud-text-sm">{showBlendedOfferBadge ? interpolate(gettext("Access %(courses_count)s+ Premium courses"), {
        courses_count: formatNumber(isStarterCourseInTiers ? subscriptionPlanStats.numberOfCourses : subscriptionTiersEnabled ? personalPlanCourseCount : subscriptionPlanStats.numberOfCourses)
      }, true) : isStarterCourseInTiers && clpGenericUdemyPlansCopyEnabled ? interpolate(gettext("Access up to %(courses_count)s+ courses with a Udemy subscription."), {
        courses_count: formatNumber(personalPlanCourseCount)
      }, true) : interpolate(gettext("Access to %(courses_count)s+ top-rated courses"), {
        courses_count: formatNumber(isStarterCourseInTiers ? subscriptionPlanStats.numberOfCourses : subscriptionTiersEnabled ? personalPlanCourseCount : subscriptionPlanStats.numberOfCourses)
      }, true)}</span></BlockList.Item>;
  const t20 = showBlendedOfferBadge ? "tick" : subscriptionTiersEnabled ? "cancel" : "coupon";
  let t21;
  if ($[49] !== t20) {
    t21 = <Icon label="" size="xsmall" glyph={t20} />;
    $[49] = t20;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== gettext) {
    t22 = gettext("Cancel anytime");
    $[51] = gettext;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] !== t22) {
    t23 = <span className="ud-text-sm">{t22}</span>;
    $[53] = t22;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== t21 || $[56] !== t23) {
    t24 = <BlockList.Item icon={t21}>{t23}</BlockList.Item>;
    $[55] = t21;
    $[56] = t23;
    $[57] = t24;
  } else {
    t24 = $[57];
  }
  let t25;
  if ($[58] !== categoryStats.isSubCopyEnabled || $[59] !== gettext || $[60] !== handleLearnMoreClick || $[61] !== learnMoreTarget || $[62] !== learnMoreText || $[63] !== learnMoreUrl || $[64] !== showBlendedOfferBadge) {
    t25 = !categoryStats.isSubCopyEnabled && <BlockList.Item icon={<Icon label="" size="xsmall" glyph="info-outline" />}><a className={showBlendedOfferBadge ? styles["learn-more-link-blended"] : styles["learn-more-link"]} data-purpose="subscription-learn-more-link" href={learnMoreUrl} target={learnMoreTarget} onClick={handleLearnMoreClick}>{showBlendedOfferBadge ? gettext("Learn more about Personal Plan") : learnMoreText}</a></BlockList.Item>;
    $[58] = categoryStats.isSubCopyEnabled;
    $[59] = gettext;
    $[60] = handleLearnMoreClick;
    $[61] = learnMoreTarget;
    $[62] = learnMoreText;
    $[63] = learnMoreUrl;
    $[64] = showBlendedOfferBadge;
    $[65] = t25;
  } else {
    t25 = $[65];
  }
  let t26;
  if ($[66] !== T1 || $[67] !== t17 || $[68] !== t18 || $[69] !== t19 || $[70] !== t24 || $[71] !== t25) {
    t26 = <T1 padding={t15} size={t16}>{t17}{t18}{t19}{t24}{t25}</T1>;
    $[66] = T1;
    $[67] = t17;
    $[68] = t18;
    $[69] = t19;
    $[70] = t24;
    $[71] = t25;
    $[72] = t26;
  } else {
    t26 = $[72];
  }
  const T2 = SubscriptionCTA;
  let t27;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = classNames(styles["subscription-cta"], styles["subscription-cta-collapsible"]);
    $[73] = t27;
  } else {
    t27 = $[73];
  }
  const t28 = UI_REGION;
  const t29 = !hidePersonalPlanValuePropForStarterUpsell && categoryStats.isSubCopyEnabled;
  const t30 = "subscription-purchase-button";
  const t31 = "primary";
  const t32 = parseInt(course.id, 10);
  const t33 = false;
  let t34;
  if ($[74] !== subscriptionCTAConfiguration || $[75] !== subscriptionPriceBoxNotice) {
    t34 = subscriptionCTAConfiguration?.upgradeUrl ? {
      ...subscriptionPriceBoxNotice,
      cta_1_url: subscriptionCTAConfiguration.upgradeUrl
    } : subscriptionPriceBoxNotice;
    $[74] = subscriptionCTAConfiguration;
    $[75] = subscriptionPriceBoxNotice;
    $[76] = t34;
  } else {
    t34 = $[76];
  }
  const t35 = subscriptionCTAConfiguration?.directCheckout;
  const t36 = subscriptionCTAConfiguration?.selectPlanText;
  const t37 = "Join 5 lakh+ learners on Personal Plan today";
  const t38 = subscriptionCTAConfiguration?.purchaseCtaText || ctaText(changeCtaLangFeatureAssignment);
  let t39;
  if ($[77] !== T2 || $[78] !== handleCtaClick || $[79] !== handleOfferImpression || $[80] !== t27 || $[81] !== t28 || $[82] !== t29 || $[83] !== t32 || $[84] !== t34 || $[85] !== t35 || $[86] !== t36 || $[87] !== t38 || $[88] !== unwrappedConsumerSubscriptionPlan) {
    t39 = <T2 className={t27} uiRegion={t28} showSubtitle={t29} data-purpose={t30} offerCtaStyle={t31} onCTAClick={handleCtaClick} trackImpression={handleOfferImpression} courseId={t32} includeTrialDaysOnCta={t33} plan={unwrappedConsumerSubscriptionPlan} noticeData={t34} directCheckout={t35} selectPlanText={t36} customSubtitle={t37} purchaseCtaText={t38} />;
    $[77] = T2;
    $[78] = handleCtaClick;
    $[79] = handleOfferImpression;
    $[80] = t27;
    $[81] = t28;
    $[82] = t29;
    $[83] = t32;
    $[84] = t34;
    $[85] = t35;
    $[86] = t36;
    $[87] = t38;
    $[88] = unwrappedConsumerSubscriptionPlan;
    $[89] = t39;
  } else {
    t39 = $[89];
  }
  let t40;
  if ($[90] !== T0 || $[91] !== handleExpand || $[92] !== isPersonalPlanPrimaryPurchaseOptions || $[93] !== t14 || $[94] !== t26 || $[95] !== t39 || $[96] !== title) {
    t40 = <T0 expanded={isPersonalPlanPrimaryPurchaseOptions} title={title} onExpand={handleExpand}>{t14}{t26}{t39}</T0>;
    $[90] = T0;
    $[91] = handleExpand;
    $[92] = isPersonalPlanPrimaryPurchaseOptions;
    $[93] = t14;
    $[94] = t26;
    $[95] = t39;
    $[96] = title;
    $[97] = t40;
  } else {
    t40 = $[97];
  }
  return t40;
};
export const CollapsibleSubscriptionPurchaseContent = React.memo(CollapsibleSubscriptionPurchaseContentInner);
function _temp(contentCollection) {
  return contentCollection.stats;
}